import { useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ConfigContext } from '../shared/ConfigContext';
import { usePlanLimitContent } from '../help/planLimitContent';

const PAGE_SIZE = 25;
const EMPTY_FORM = { productName: '', productPrice: '', productDescription: '', unit: '', categoryId: '', videoUrl: '' };

const isValidUrl = (value) => {
  if (!value) return true;
  try {
    // eslint-disable-next-line no-new
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

// All state and handlers for the Products page, kept out of Products.js so
// that file stays a thin JSX composition — the pattern Step 5 pages copy.
const useProductsPage = () => {
  const { t, i18n } = useTranslation();
  const config = useContext(ConfigContext);
  const [searchParams] = useSearchParams();
  const previewState = searchParams.get('previewState'); // loading|empty|no-match|error|permission — devtools review only
  // Step 9: same wording as the meter's own "?" and the help panel's limit
  // article (components/help/planLimitContent.js) — never a locally-composed string.
  const planLimit = usePlanLimitContent('products.max');

  const [companyId] = useState(() => localStorage.getItem('companyId') || '');
  const [categories, setCategories] = useState([]);

  const [rows, setRows] = useState([]);
  const rowsRef = useRef(rows);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  const [total, setTotal] = useState(0);
  const [status, setStatus] = useState('loading');
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorInfo, setErrorInfo] = useState(null);

  const [sortModel, setSortModel] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selection, setSelection] = useState([]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create');
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [stagedFiles, setStagedFiles] = useState(null); // FileList of newly picked images, or null to keep existing
  const [editingProductId, setEditingProductId] = useState(null);
  const [existingImages, setExistingImages] = useState([]);
  const [saving, setSaving] = useState(false);

  const [rowMenuAnchor, setRowMenuAnchor] = useState(null);
  const [rowMenuProduct, setRowMenuProduct] = useState(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTargets, setConfirmTargets] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [inventoryByProduct, setInventoryByProduct] = useState({});

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkCategoryId, setBulkCategoryId] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const notify = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

  // Debounce search → server request, same param the drawer/table both read.
  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    if (!config?.backendUrl || !companyId) return;
    axios.get(`${config.backendUrl}/categories?companyId=${companyId}`)
      .then((res) => setCategories(res.data))
      .catch((error) => console.error('Error fetching categories', error));
  }, [config, companyId]);

  // `reset` replaces the loaded rows with page 1 (search/filter/sort
  // changed); otherwise the next page is derived from how many rows are
  // already loaded and appended — the "Load more" pattern the design uses
  // instead of a numbered pager.
  const fetchProducts = useCallback((reset = true) => {
    if (!config?.backendUrl || !companyId) return;
    if (reset) setStatus('loading'); else setLoadingMore(true);
    const page = reset ? 1 : Math.floor(rowsRef.current.length / PAGE_SIZE) + 1;
    const params = { companyId, page, pageSize: PAGE_SIZE };
    if (categoryFilter !== 'all') params.categoryId = categoryFilter;
    if (search) params.search = search;
    if (sortModel[0]) {
      params.sortBy = sortModel[0].field;
      params.sortDir = sortModel[0].sort;
    }
    axios.get(`${config.backendUrl}/products`, { params })
      .then((res) => {
        setRows((prev) => (reset ? res.data.items : [...prev, ...res.data.items]));
        setTotal(res.data.total);
        setStatus('ready');
      })
      .catch((error) => {
        console.error('Error fetching products', error);
        if (error.response?.status === 403) {
          setStatus('permission');
        } else {
          setErrorInfo({
            request: `GET /products · ${error.response?.status || t('N/A')} · ${new Date().toLocaleTimeString()}`,
          });
          setStatus('error');
        }
      })
      .finally(() => setLoadingMore(false));
  }, [config, companyId, categoryFilter, search, sortModel, t]);

  useEffect(() => { fetchProducts(true); }, [fetchProducts]);

  const loadMore = () => fetchProducts(false);
  const remaining = Math.max(0, total - rows.length);
  const nextBatchSize = Math.min(PAGE_SIZE, remaining);

  const categoryById = useMemo(() => {
    const map = {};
    categories.forEach((c) => { map[c._id] = c; });
    return map;
  }, [categories]);

  // ---- Row actions menu ----------------------------------------------- //
  const openRowMenu = (event, product) => {
    event.stopPropagation();
    setRowMenuAnchor(event.currentTarget);
    setRowMenuProduct(product);
  };
  const closeRowMenu = () => {
    setRowMenuAnchor(null);
    setRowMenuProduct(null);
  };

  // ---- Create / edit drawer -------------------------------------------- //
  const openCreateDrawer = () => {
    setDrawerMode('create');
    setForm(EMPTY_FORM);
    setFormErrors({});
    setStagedFiles(null);
    setExistingImages([]);
    setEditingProductId(null);
    setDrawerOpen(true);
  };

  const openEditDrawer = (product) => {
    setDrawerMode('edit');
    setForm({
      productName: product.productName,
      productPrice: String(product.productPrice ?? ''),
      productDescription: product.productDescription || '',
      unit: product.unit || '',
      categoryId: product.categoryId || '',
      videoUrl: product.productVideo || '',
    });
    setFormErrors({});
    setStagedFiles(null);
    setExistingImages(product.productImages || (product.productImage ? [product.productImage] : []));
    setEditingProductId(product._id);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    if (saving) return;
    setDrawerOpen(false);
  };

  // Each pick adds to what is already staged, so images can be added over
  // several picks; clearing the input lets the same file be picked again.
  const handleFileInput = (event) => {
    const dt = new DataTransfer();
    [...Array.from(stagedFiles || []), ...Array.from(event.target.files || [])].forEach((f) => dt.items.add(f));
    event.target.value = '';
    setStagedFiles(dt.files.length ? dt.files : null);
  };

  const stagedPreviews = useMemo(
    () => (stagedFiles ? Array.from(stagedFiles).map((f) => URL.createObjectURL(f)) : []),
    [stagedFiles]
  );
  // Index is into displayedImages: kept existing images first, then staged files.
  const removeStagedFile = (displayIndex) => {
    if (displayIndex < existingImages.length) {
      setExistingImages(existingImages.filter((_, i) => i !== displayIndex));
      return;
    }
    const index = displayIndex - existingImages.length;
    if (!stagedFiles) return;
    const remaining = Array.from(stagedFiles).filter((_, i) => i !== index);
    if (remaining.length === 0) {
      setStagedFiles(null);
      return;
    }
    const dt = new DataTransfer();
    remaining.forEach((f) => dt.items.add(f));
    setStagedFiles(dt.files);
  };

  const displayedImages = [...existingImages, ...stagedPreviews];

  const validateForm = () => {
    const errors = {};
    if (!form.productName.trim()) errors.productName = t('Product Name') + ' ' + t('is required', { defaultValue: 'is required' });
    const priceNum = Number(form.productPrice);
    if (!form.productPrice || Number.isNaN(priceNum) || priceNum <= 0) {
      errors.productPrice = t('Price must be greater than zero', { defaultValue: 'Price must be greater than zero' });
    }
    // A deleted category still shows as "Unknown"; treat it as unset so the
    // user is asked to pick one instead of the save failing server-side.
    if (!form.categoryId || !categoryById[form.categoryId]) {
      errors.categoryId = t('Category') + ' ' + t('is required', { defaultValue: 'is required' });
    }
    if (!isValidUrl(form.videoUrl)) {
      errors.videoUrl = t('Enter a valid URL', { defaultValue: 'Enter a valid URL' });
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildFormData = () => {
    const formData = new FormData();
    if (stagedFiles && stagedFiles.length) {
      Array.from(stagedFiles).forEach((file) => formData.append('productImages', file));
    }
    // JSON so an empty list (every existing image removed) still reaches the backend.
    if (drawerMode === 'edit') formData.append('existingImages', JSON.stringify(existingImages));
    formData.append('productName', form.productName);
    formData.append('productPrice', form.productPrice);
    formData.append('productDescription', form.productDescription);
    formData.append('unit', form.unit);
    formData.append('companyId', companyId);
    if (form.categoryId) formData.append('categoryId', form.categoryId);
    if (form.videoUrl) formData.append('videoUrl', form.videoUrl);
    return formData;
  };

  const handleSave = async () => {
    if (saving || !validateForm()) return;
    setSaving(true);
    try {
      if (drawerMode === 'create') {
        await axios.post(`${config.backendUrl}/products`, buildFormData(), {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        notify(t('Product Added Successfully!'));
      } else {
        await axios.put(`${config.backendUrl}/products/${editingProductId}`, buildFormData(), {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        notify(t('Product Updated Successfully!'));
      }
      setDrawerOpen(false);
      fetchProducts();
    } catch (error) {
      console.error(error);
      notify(drawerMode === 'create' ? t('Error creating product') : t('Error updating product'), 'error');
    } finally {
      setSaving(false);
    }
  };

  // ---- Delete confirm ---------------------------------------------------//
  const openDeleteConfirm = async (products) => {
    setConfirmTargets(products);
    try {
      const res = await axios.get(`${config.backendUrl}/inventory?companyId=${companyId}`);
      const byProduct = {};
      (Array.isArray(res.data) ? res.data : []).forEach((item) => {
        byProduct[item.productId] = (byProduct[item.productId] || 0) + (item.quantity || 0);
      });
      setInventoryByProduct(byProduct);
    } catch (error) {
      console.error('Error fetching inventory for delete confirm', error);
      setInventoryByProduct({});
    }
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    setDeleting(true);
    try {
      await Promise.all(confirmTargets.map((p) => axios.delete(`${config.backendUrl}/products/${p._id}`)));
      notify(t('Product(s) successfully removed!'));
      setSelection([]);
      setConfirmOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error removing products:', error);
      notify(t('Error removing products'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ---- Bulk category change --------------------------------------------//
  const selectedProducts = useMemo(() => rows.filter((p) => selection.includes(p._id)), [rows, selection]);

  const openBulkCategory = () => {
    setBulkCategoryId('');
    setBulkOpen(true);
  };

  const handleBulkCategorySave = async () => {
    setBulkSaving(true);
    try {
      await Promise.all(selectedProducts.map((p) => {
        const formData = new FormData();
        formData.append('categoryId', bulkCategoryId);
        return axios.put(`${config.backendUrl}/products/${p._id}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }));
      notify(t('Categories updated for {{count}} products', { count: selectedProducts.length }));
      setBulkOpen(false);
      setSelection([]);
      fetchProducts();
    } catch (error) {
      console.error('Error updating categories:', error);
      notify(t('Error updating categories'), 'error');
    } finally {
      setBulkSaving(false);
    }
  };

  // ---- CSV export ---------------------------------------------------- //
  const handleExport = async () => {
    const params = { companyId };
    if (categoryFilter !== 'all') params.categoryId = categoryFilter;
    if (search) params.search = search;
    try {
      const res = await axios.get(`${config.backendUrl}/products/export`, { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'products.csv';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting products', error);
      notify(t('Error exporting products', { defaultValue: 'Error exporting products' }), 'error');
    }
  };

  // DataTableCard only recognises loading/error/permission/ready as `status`
  // — the empty/no-match distinction is a sub-state of "ready" driven by
  // `empty.kind`, so the ?previewState= override has to route through that
  // instead of passing 'empty'/'no-match' straight through as a status.
  const isPreviewEmptyKind = previewState === 'empty' || previewState === 'no-match';
  const effectiveStatus = isPreviewEmptyKind ? 'ready' : (previewState || status);
  const effectiveRows = isPreviewEmptyKind ? [] : (effectiveStatus === 'ready' ? rows : []);
  const isNoMatch = isPreviewEmptyKind ? previewState === 'no-match' : (search || categoryFilter !== 'all');

  return {
    t, i18n, companyId, categories, categoryById,
    rows, total, errorInfo, effectiveStatus, effectiveRows, isNoMatch, search, setSearch, searchInput, setSearchInput,
    categoryFilter, setCategoryFilter, sortModel, setSortModel,
    loadingMore, loadMore, remaining, nextBatchSize,
    selection, setSelection,
    drawerOpen, setDrawerOpen, drawerMode, form, setForm, formErrors, stagedFiles, saving, displayedImages,
    handleFileInput, removeStagedFile, openCreateDrawer, openEditDrawer, closeDrawer, handleSave,
    rowMenuAnchor, rowMenuProduct, openRowMenu, closeRowMenu,
    confirmOpen, setConfirmOpen, confirmTargets, deleting, inventoryByProduct, openDeleteConfirm, handleConfirmDelete,
    bulkOpen, setBulkOpen, bulkCategoryId, setBulkCategoryId, bulkSaving, openBulkCategory, handleBulkCategorySave,
    selectedProducts, editingProductId,
    handleExport, snackbar, setSnackbar, fetchProducts,
    planLimit,
  };
};

export default useProductsPage;
